import { ObjectPool, ObjectPoolItem } from "game-common/object_pool"
import { randomId } from "game-common/util"
import { Sprite, Texture } from "pixi.js"

export interface SpritePoolItem extends ObjectPoolItem<Sprite> {}

export class SpritePool extends ObjectPool<Sprite> {
    static instance: SpritePool
    private defaultSprite: Sprite
    private poolStats = {
        totalCreated: 0,
        totalReused: 0,
        totalCleared: 0,
        peakUsage: 0,
        currentUsage: 0,
    }

    // Properties from parent class
    items: ObjectPoolItem<Sprite>[] = []
    ptr: number = -1
    releasing: boolean = false

    constructor(size: number = 1000) {
        super(size)
        this.defaultSprite = new Sprite()
    }

    static getInstance(): SpritePool {
        if (!SpritePool.instance) {
            SpritePool.instance = new SpritePool()
        }
        return SpritePool.instance
    }

    create(): SpritePoolItem {
        // Initialize poolStats if not already initialized (happens during construction)
        if (!this.poolStats) {
            this.poolStats = {
                totalCreated: 0,
                totalReused: 0,
                totalCleared: 0,
                peakUsage: 0,
                currentUsage: 0,
            }
        }

        this.poolStats.totalCreated++
        this.poolStats.currentUsage++
        this.poolStats.peakUsage = Math.max(this.poolStats.peakUsage, this.poolStats.currentUsage)

        return {
            free: true,
            data: new Sprite(),
        }
    }

    checkout = (): SpritePoolItem => {
        // Call parent checkout method directly
        if (this.releasing) {
            throw new Error("BAD!")
        }
        this.ptr++
        if (this.ptr > this.items.length - 1) {
            // grow the array by creating new items
            for (let i = 0; i < 1000; i++) {
                this.items.push(this.create())
            }
        }
        const item = this.items[this.ptr]
        item.free = false

        // Update stats (ensure poolStats is initialized)
        if (this.poolStats) {
            this.poolStats.totalReused++
            this.poolStats.currentUsage++
            this.poolStats.peakUsage = Math.max(this.poolStats.peakUsage, this.poolStats.currentUsage)
        }
        return item
    }

    release = (item: SpritePoolItem): void => {
        // Call parent release method directly
        if (item.free) {
            // already free, noop
            return
        }

        item.free = true
        this.clear(item)

        this.ptr--
        if (this.ptr < 0) {
            this.ptr = 0
        }

        // Update stats (ensure poolStats is initialized)
        if (this.poolStats) {
            this.poolStats.currentUsage--
        }
    }

    clear(item: SpritePoolItem) {
        // Update stats (ensure poolStats is initialized)
        if (this.poolStats) {
            this.poolStats.totalCleared++
        }

        // Reset all properties to default state
        item.data.texture = Texture.EMPTY
        item.data.x = this.defaultSprite.x
        item.data.y = this.defaultSprite.y
        item.data.tint = this.defaultSprite.tint
        item.data.visible = this.defaultSprite.visible
        item.data.alpha = this.defaultSprite.alpha
        item.data.scale.set(this.defaultSprite.scale.x, this.defaultSprite.scale.y)
        item.data.rotation = this.defaultSprite.rotation
        item.data.zIndex = this.defaultSprite.zIndex
        item.data.removeAllListeners()
        item.data.interactive = false
        item.data.buttonMode = false

        // Clear any filters
        item.data.filters = null

        // Reset anchor
        item.data.anchor.set(0, 0)

        // Remove from parent if attached
        if (item.data.parent) {
            item.data.parent.removeChild(item.data)
        }
    }

    getStats() {
        return {
            ...this.poolStats,
            reuseRate:
                this.poolStats.totalCreated > 0
                    ? ((this.poolStats.totalReused / this.poolStats.totalCreated) * 100).toFixed(2) + "%"
                    : "0%",
            currentPoolSize: this.items.length,
            freeItems: this.items.filter(item => item.free).length,
            usedItems: this.items.filter(item => !item.free).length,
        }
    }

    resetStats() {
        this.poolStats = {
            totalCreated: 0,
            totalReused: 0,
            totalCleared: 0,
            peakUsage: 0,
            currentUsage: 0,
        }
    }
}
